import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { fetchUserData } from '../../../../../redux/slices/authThunk';

// API Calls
import { getNodeTypes } from '../../../../../redux/slices/lists/nodeTypesSlice';
import { getZones } from '../../../../../redux/slices/lists/zonesSlice';
import { getAuthorizedZones } from '../../../../../redux/slices/lists/zonesSlice';
import { getFolders } from '../../../../../redux/slices/lists/foldersSlice';
import {
  createCustomerNodeDefinition,
  fetchCustomerNodeDefinitionById,
  updateCustomerNodeDefinition,
} from '../../../../../redux/slices/customer_node_definitionsSlice';

// Interface
import { NewNode } from '../../../../../interfaces/nodeDefinitions';
import { buildFolderPathOptions } from '../../../../../helpers/folderPaths';

import { LoadingInput, LoadingSelect } from 'components/forms/LoadingFields';

const INITIAL_NEW_NODE = {
  name: '',
  zone_name: '',
  type: '',
  sub_type: 'bash',
  folder: '',
};

const sortZonesByName = (zones: any[] = []) => [...zones].sort((a, b) => a.name.localeCompare(b.name));

const resolveZoneSelection = (zones: any[] = [], preferredZoneName: string | null | undefined) => {
  const sortedZones = sortZonesByName(zones);
  const fallbackZone = sortedZones[0]?.name || '';
  const normalizedPreferred = preferredZoneName?.trim();

  if (!normalizedPreferred || normalizedPreferred === 'None') {
    return fallbackZone;
  }

  const preferredExists = sortedZones.some((zone) => zone.name === normalizedPreferred);

  return preferredExists ? normalizedPreferred : fallbackZone;
};

// Global handler
const handleCancel = (e) => {
  e.preventDefault();
  window.close();
};

// Create new node definition flow
const CreateNodeDefinitionEditor = () => {
  const [listOfNodeTypes, setListOfNodeTypes] = useState({ node_types: [] });
  const [listOfZones, setListOfZones] = useState({ zones: [] });
  const [state, setState] = useState<NewNode>(INITIAL_NEW_NODE);
  const [showError, setShowError] = useState<string | null>(null);

  const [isNodeTypesLoading, setIsNodeTypesLoading] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState<any>(null);

  const isZonesLoading = useSelector((s: any) => s.listOfZones?.loading?.getAuthorizedZones);
  const isFoldersLoading = useSelector((s: any) => s.listOfFolders?.loading?.getFolders);
  const userDefaultZone = useSelector((s: any) => s.auth?.userData?.user_settings?.default_zone_name);
  const isUserDataLoaded = useSelector((s: any) => !!s.auth?.userData);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const duplicateFromIdRaw = searchParams.get('duplicateFromId');
  const duplicateFromId = duplicateFromIdRaw ? Number(duplicateFromIdRaw) : null;

  useEffect(() => {
    if (isUserDataLoaded) return;
    dispatch(fetchUserData() as any);
  }, [dispatch, isUserDataLoaded]);

  useEffect(() => {
    const fetchNodeTypes = async () => {
      try {
        setIsNodeTypesLoading(true);
        const response: any = await dispatch(getNodeTypes());
        if (response) {
          const { payload } = response;
          const { node_types = [] } = payload;

          setListOfNodeTypes({ node_types });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsNodeTypesLoading(false);
      }
    };

    const fetchZones = async () => {
      try {
        const response: any = await dispatch(
          getAuthorizedZones({
            resourceType: 'node_definitions',
            action: 'create',
          } as any)
        );
        if (response) {
          const { payload } = response;
          const { zones = [] } = payload;
          setListOfZones({ zones });
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchFolders = async () => {
      try {
        await dispatch(getFolders());
      } catch (error) {
        console.error(error);
      }
    };

    fetchNodeTypes();
    fetchZones();
    fetchFolders();
  }, []);

  useEffect(() => {
    const fetchDuplicateSource = async () => {
      if (!duplicateFromId) return;

      try {
        setIsDuplicateLoading(true);
        const response: any = await dispatch(fetchCustomerNodeDefinitionById(Number(duplicateFromId)));

        const payload = response?.payload;
        if (!payload || payload?.error) {
          setShowError(payload?.error || 'Failed to load node definition to duplicate.');
          return;
        }

        setDuplicateSource(payload);

        setState((prev) => ({
          ...prev,
          name: '',
          type: payload.type || prev.type,
          zone_name: payload.zone_name || prev.zone_name,
          sub_type: payload.sub_type || payload?.config?.sub_type || prev.sub_type || 'bash',
          folder: payload.folder ?? payload.folder_name ?? prev.folder ?? '',
        }));
      } catch (error) {
        console.error(error);
        setShowError(String(error));
      } finally {
        setIsDuplicateLoading(false);
      }
    };

    fetchDuplicateSource();
  }, [dispatch, duplicateFromId]);

  useEffect(() => {
    // A Workspace-provided zone (?zoneName) takes precedence over the user's
    // default. (The node-definition folder field is a path string, not a
    // folder_id, so it isn't seeded from the workspace's folder target.)
    const zoneFromQuery = searchParams.get('zoneName');
    const preferredZone = resolveZoneSelection(listOfZones.zones, zoneFromQuery || userDefaultZone);
    if (!preferredZone) return;

    setState((prev) => {
      if (prev.zone_name && listOfZones.zones.some((zone) => zone.name === prev.zone_name)) {
        return prev;
      }

      return { ...prev, zone_name: preferredZone };
    });
  }, [listOfZones.zones, userDefaultZone, searchParams]);

  const AlertDialog = ({ error, setError }) => {
    if (!showError) return null;
    return (
      <>
        <Alert className="mt-4" onClose={() => setError(null)} variant="soft-danger" dismissible>
          <Alert.Heading>An error has occurred!</Alert.Heading>
          <p>{showError}</p>
        </Alert>
      </>
    );
  };

  const handleCreateNodeDefinition = async (e) => {
    e.preventDefault();
    try {
      setShowError(null);

      // Step 1: create the shell definition (name/type/zone)
      const response: any = await dispatch(createCustomerNodeDefinition(state));
      if (createCustomerNodeDefinition.rejected.match(response)) {
        return setShowError(String(response.payload));
      }

      const { node_definition_id } = response?.payload || {};
      if (!node_definition_id) {
        return setShowError('Creation failed: missing node_definition_id');
      }

      // Step 2: if duplicating, copy over all values from the source (except the name + id)
      if (duplicateFromId && duplicateSource) {
        const source = duplicateSource;

        const transformedChildNodes = (source.child_nodes || []).map((node) => ({
          ...node,
          node_definition_name: node.node_definition_name || node.name,
          zone_name: node.zone_name || state.zone_name,
        }));

        const payloadToUpdate = {
          ...source,
          version: response?.payload?.version || 1,
          node_definition_id,
          name: state.name,
          zone_name: state.zone_name,
          type: state.type,
          sub_type: state.type === 'shell' ? state.sub_type || 'bash' : null,
          child_nodes: transformedChildNodes,
        };

        const updateResp: any = await dispatch(
          updateCustomerNodeDefinition({
            definition: payloadToUpdate,
            originalZoneName: state.zone_name,
            originalName: state.name,
          })
        );

        if (updateCustomerNodeDefinition.rejected.match(updateResp)) {
          return setShowError(String(updateResp.payload));
        }
      }

      const url = `/node_editor/${node_definition_id}/${state.name}`;
      return navigate(url);
    } catch (error) {
      console.error('Creation failed:', error);
      setShowError(String(error));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    return setState((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'type') {
        if (value === 'shell') next.sub_type = next.sub_type || 'bash';
        else next.sub_type = null;
      }

      if (name === 'zone_name') {
        next.folder = '';
      }

      return next;
    });
  };

  const { node_types } = listOfNodeTypes;
  const { zones } = listOfZones;
  const sortedZones = sortZonesByName(zones);
  const fallbackZone = sortedZones[0]?.name || '';
  const folders = useSelector((s: any) => s.listOfFolders?.folders ?? []);
  const folderPathOptions = buildFolderPathOptions(folders, state.zone_name || fallbackZone);

  const isCreateDisabled = isNodeTypesLoading || isZonesLoading || isDuplicateLoading;

  return (
    <Container fluid>
      <AlertDialog error={showError} setError={setShowError} />

      <Form>
        <Row className="mt-4 mb-4 justify-content-lg-center">
          <Col lg="10">
            <h2>{duplicateFromId ? `Duplicate node definition: ${duplicateSource?.name || ''}` : 'Create new node definition'}</h2>
          </Col>
        </Row>
        <Row className="mt-4 mb-4 justify-content-lg-center">
          <Col lg="10">
            <LoadingInput
              label="Name"
              controlId="formNodeName"
              aria-label="Select node name"
              type="text"
              name="name"
              value={state.name}
              onChange={handleChange}
              isLoading={false}
              groupClassName="mb-2 w-full"
            />

            <LoadingSelect
              label="Type"
              controlId="formNodeType"
              name="type"
              value={state.type}
              onChange={handleChange}
              isLoading={isNodeTypesLoading}
              options={(node_types || []).map((node: any) => ({
                value: node.node_type,
                label: node.node_type === 'shell' ? 'Shell' : node.node_type,
              }))}
              groupClassName="mb-2 w-full"
              placeholder="Select type"
              aria-label="Select node type"
            />

            {state.type === 'shell' && (
              <LoadingSelect
                label="Shell type"
                controlId="formNodeSubType"
                name="sub_type"
                value={state.sub_type || 'bash'}
                onChange={handleChange}
                isLoading={false}
                options={[
                  { value: 'bash', label: 'bash' },
                  { value: 'ksh', label: 'ksh' },
                  { value: 'cmd', label: 'cmd' },
                  { value: 'pwsh', label: 'pwsh' },
                ]}
                groupClassName="mb-2 w-full"
                placeholder="Select sub type"
                aria-label="Select node sub type"
              />
            )}

            <LoadingSelect
              label="Zone"
              controlId="formZone"
              name="zone_name"
              value={state.zone_name || fallbackZone}
              onChange={handleChange}
              isLoading={isZonesLoading}
              options={sortedZones.map((z: any) => ({
                value: z.name,
                label: z.name,
              }))}
              groupClassName="mb-2 w-full"
              placeholder="Select zone"
              aria-label="Select zone"
            />

            <LoadingSelect
              label="Folder (path)"
              controlId="formFolder"
              name="folder"
              value={state.folder || ''}
              onChange={handleChange}
              isLoading={isFoldersLoading}
              options={folderPathOptions}
              groupClassName="mb-2 w-full"
              placeholder="Select folder"
              aria-label="Select folder"
            />
          </Col>
        </Row>
        <Row className="pt-4 justify-content-lg-center border-top">
          <Col lg="10">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button className="ms-2" onClick={handleCreateNodeDefinition} type="submit" variant="primary" disabled={isCreateDisabled}>
              {duplicateFromId ? 'Create duplicate' : 'Create'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default CreateNodeDefinitionEditor;
